import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../../../services/api';

interface Step2A_OwnedStocksProps {
  selectedStocks: string[];
  onStocksChange: (stocks: string[]) => void;
  onSkip: () => void;
  onContinue: () => void;
}

const Step2A_OwnedStocks: React.FC<Step2A_OwnedStocksProps> = ({
  selectedStocks,
  onStocksChange,
  onSkip,
  onContinue
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<{symbol: string; name: string}[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const filterSuggestions = async (input: string) => {
    if (!input || input.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await apiService.getSymbolsWithOptions(input, 10);

      if (response.status === 'success') {
        const results = response.symbols.map((item) => ({
          symbol: item.symbol,
          name: item.name
        }));
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (value: string) => {
    const upperValue = value.toUpperCase();
    setSearchValue(upperValue);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      filterSuggestions(upperValue);
    }, 300);
  };

  const addStock = (symbol: string) => {
    if (!selectedStocks.includes(symbol)) {
      onStocksChange([...selectedStocks, symbol]);
    }
    setSearchValue('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeStock = (symbol: string) => {
    onStocksChange(selectedStocks.filter(s => s !== symbol));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0) {
          addStock(suggestions[highlightedIndex].symbol);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }
  };

  return (
    <div className="wizard-step">
      <div className="wizard-step-content">
        <h2 className="wizard-question">
          Which stocks do you own?
        </h2>

        <div className="wizard-typeahead-container">
          <div className="wizard-typeahead-wrapper">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (searchValue.length > 0) {
                  filterSuggestions(searchValue);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 150);
              }}
              placeholder="Search for stocks (e.g., AAPL, MSFT, TSLA)..."
              className="wizard-typeahead-input"
              aria-label="Search for stocks you own"
            />

            {showSuggestions && suggestions.length > 0 && (
              <div className="wizard-typeahead-suggestions">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.symbol}
                    className={`wizard-typeahead-suggestion ${
                      index === highlightedIndex ? 'highlighted' : ''
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addStock(suggestion.symbol);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span className="suggestion-symbol">{suggestion.symbol}</span>
                    <span className="suggestion-name">{suggestion.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected stocks as chips */}
          {selectedStocks.length > 0 && (
            <div className="wizard-selected-chips">
              {selectedStocks.map((stock) => (
                <div key={stock} className="wizard-chip">
                  <span className="chip-text">{stock}</span>
                  <button
                    className="chip-remove"
                    onClick={() => removeStock(stock)}
                    aria-label={`Remove ${stock}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="wizard-helper-text">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', marginRight: '6px', verticalAlign: 'text-bottom'}}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          Add all the stocks you own. We'll show income opportunities for each.
        </p>

        <div className="wizard-typeahead-actions">
          <button
            className="wizard-skip-button"
            onClick={onSkip}
          >
            Skip for now
          </button>
          <button
            className="wizard-next-button"
            onClick={onContinue}
            disabled={selectedStocks.length === 0}
          >
            {selectedStocks.length > 0 ? `Continue with ${selectedStocks.length} stock${selectedStocks.length > 1 ? 's' : ''}` : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step2A_OwnedStocks;
